import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useFamilyClassificationJob, type FamilyJobState } from "../hooks/useFamilyClassificationJob";
import {
  cancelJob,
  estimateFamilies,
  getFamilySettings,
  saveFamilyModel,
  setFamilyPrefix,
  startFamilyClassification,
} from "../lib/api";
import { shortModel, usd } from "../lib/leads";
import type { FamilyEstimate, FamilyOverview, FamilyRunSummary, FamilySettings, OpenRouterModel } from "../lib/types";
import { formatApiError } from "../lib/utils";
import { Icon } from "./Icon";
import { useToast } from "./Toast";
import { Button, Input, Select } from "./ui";

type Mode = "incremental" | "rebuild";

const STAGE_LABELS: Record<string, string> = {
  loading: "Loading projects",
  cards: "Building project cards",
  calling_model: "Asking the model",
  validating: "Checking the answer",
  validated: "Checked",
  writing: "Writing",
  done: "Done",
};

const FIELD_LABELS: Record<string, string> = {
  project_family: "Family",
  project_family_id: "ID",
  project_family_justification: "Justification",
  project_family_justification_internal: "Tags",
};

function modelOptions(pool: OpenRouterModel[], current: string): OpenRouterModel[] {
  const list = [...pool].sort((a, b) =>
    (a.name || a.id).localeCompare(b.name || b.id, undefined, { sensitivity: "base" }),
  );
  if (current && !list.some((m) => m.id === current)) list.unshift({ id: current, name: current });
  return list;
}

function finishedMessage(state: FamilyJobState): { text: string; error: boolean } {
  const summary = state.progress?.summary;
  if (state.status === "cancelled") return { text: "Family run stopped before writing.", error: true };
  if (state.status !== "ok" || !summary) {
    return { text: state.error || `Family run ended: ${state.status}`, error: true };
  }
  if (summary.skipped) return { text: summary.message || "Another family run holds this bank.", error: true };
  if (summary.status === "no_projects") return { text: "This bank has no active projects.", error: true };
  const verb = summary.dry_run ? "would change" : "changed";
  const changed = summary.dry_run ? summary.changes_total ?? 0 : summary.written ?? 0;
  return {
    text:
      `${summary.dry_run ? "Dry run" : "Families written"}: ${summary.families ?? 0} families, ` +
      `${summary.duplicates_total ?? 0} duplicates, ${changed} projects ${verb} · ${usd(summary.cost_usd, 3)}.`,
    error: false,
  };
}

function SummaryList({ title, total, children }: { title: string; total: number; children: ReactNode }) {
  if (!total) return null;
  return (
    <details className="border border-outline-variant bg-surface-container-lowest px-3 py-1.5">
      <summary className="cursor-pointer text-body-sm text-on-surface">
        {title} ({total})
      </summary>
      <div className="mt-1 text-[11px] font-data-mono text-on-surface-variant space-y-0.5 max-h-64 overflow-y-auto custom-scrollbar">
        {children}
      </div>
    </details>
  );
}

/** What a finished run found: the lists an operator reviews after a dry run. */
function RunSummary({ summary, runId }: { summary: FamilyRunSummary; runId: string }) {
  const tags = Object.entries(summary.tags || {}).filter(([, n]) => n > 0);
  const variants = Object.entries(summary.variants || {}).flatMap(([kind, groups]) =>
    Object.values(groups || {}).map((forms) => ({ kind, forms })),
  );
  return (
    <div className="space-y-1.5">
      <p className="text-body-sm text-on-surface">
        {summary.dry_run ? "Dry run, nothing written" : `${summary.written ?? 0} projects written`} ·{" "}
        {summary.families ?? 0} families ({summary.multi_member ?? 0} with two or more members,{" "}
        {summary.single_member ?? 0} single) · prefix {summary.prefix || "—"}
        {summary.families_created?.length ? ` · ${summary.families_created.length} new IDs` : ""}
        {summary.families_folded?.length ? ` · ${summary.families_folded.length} folded` : ""}
        {summary.moved_total ? ` · ${summary.moved_total} moved` : ""}
        {tags.length ? ` · tags ${tags.map(([code, n]) => `${code} ${n}`).join(", ")}` : ""}
        {" · "}
        <Link to={`/logs?run=${encodeURIComponent(runId)}`} className="text-primary hover:underline">
          full log
        </Link>
      </p>
      <SummaryList title="Duplicates" total={summary.duplicates_total ?? 0}>
        {(summary.duplicates || []).map((d) => (
          <p key={d.project_id}>
            {d.project} duplicates {d.duplicate_of} ({d.family_id}): {d.evidence || "—"}
          </p>
        ))}
      </SummaryList>
      <SummaryList title="No Reason-to-Exist passed the evidence check" total={summary.unjustified_total ?? 0}>
        {(summary.unjustified || []).map((u, i) => (
          <p key={`${u.project}-${i}`}>
            {u.project} in {u.family_id} {u.family}
          </p>
        ))}
      </SummaryList>
      <SummaryList title="Tags dropped" total={summary.tags_dropped_total ?? 0}>
        {(summary.tags_dropped || []).map((t, i) => (
          <p key={`${t.project}-${t.tag}-${i}`}>
            {t.project}: {t.tag} ({t.reason})
          </p>
        ))}
      </SummaryList>
      <SummaryList title="Company differs from the bank" total={summary.company_differs_total ?? 0}>
        {(summary.company_differs || []).map((c, i) => (
          <p key={`${c.project}-${i}`}>
            {c.project}: {c.company}
          </p>
        ))}
      </SummaryList>
      <SummaryList title="Duplicates refused" total={summary.duplicates_refused?.length ?? 0}>
        {(summary.duplicates_refused || []).map((d, i) => (
          <p key={`${d.project}-${i}`}>
            {d.project} / {d.duplicate_of}: {d.reason}
          </p>
        ))}
      </SummaryList>
      <SummaryList title="Operator values kept" total={summary.pins_kept?.length ?? 0}>
        {(summary.pins_kept || []).map((p, i) => (
          <p key={`${p.project}-${i}`}>
            {p.project}: {FIELD_LABELS[p.field] || p.field} stays “{p.kept}” (proposed “{p.proposed}”)
          </p>
        ))}
      </SummaryList>
      <SummaryList title="Spelling variants" total={variants.length}>
        {variants.map((v, i) => (
          <p key={`${v.kind}-${i}`}>
            {v.kind}: {v.forms.join(" / ")}
          </p>
        ))}
      </SummaryList>
      <SummaryList title={summary.dry_run ? "Changes a run would write" : "Changes written"} total={summary.changes_total ?? 0}>
        {(summary.changes || []).map((c) => (
          <p key={c.project_id}>
            <span className="text-on-surface">{c.project}</span>:{" "}
            {Object.keys(FIELD_LABELS)
              .filter((key) => (c.before[key] || "") !== (c.after[key] || ""))
              .map((key) => `${FIELD_LABELS[key]} “${c.before[key] || "—"}” → “${c.after[key] || "—"}”`)
              .join("; ")}
          </p>
        ))}
        {(summary.changes_total ?? 0) > (summary.changes?.length ?? 0) ? (
          <p>… and {(summary.changes_total ?? 0) - (summary.changes?.length ?? 0)} more in the log.</p>
        ) : null}
      </SummaryList>
      {summary.missing?.length ? (
        <p className="text-[11px] text-error">Not placed by the model, left unchanged: {summary.missing.join(", ")}</p>
      ) : null}
    </div>
  );
}

/**
 * The family pass for one bank (plans/project-families.md): estimate, confirm, run in the
 * background, then review what it found.  A dry run writes nothing; rebuild renumbers every
 * family from 01; incremental keeps the stored families and IDs.
 */
export function FamilyClassifyToolbar({
  target,
  overview,
  onChanged,
  storageKey = "orion1.families.run",
}: {
  /** The bank's display name, as the projects list shows it. */
  target: string;
  overview: FamilyOverview | null;
  onChanged: () => void;
  storageKey?: string;
}) {
  const { toast } = useToast();
  const [settings, setSettings] = useState<FamilySettings | null>(null);
  const [model, setModel] = useState("");
  const [mode, setMode] = useState<Mode>("rebuild");
  const [dryRun, setDryRun] = useState(true);
  const [estimate, setEstimate] = useState<FamilyEstimate | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingPrefix, setEditingPrefix] = useState(false);
  const [prefixDraft, setPrefixDraft] = useState("");
  const [lastSummary, setLastSummary] = useState<{ summary: FamilyRunSummary; runId: string } | null>(null);

  const job = useFamilyClassificationJob(storageKey, (state) => {
    const message = finishedMessage(state);
    toast(message.text, message.error);
    if (state.progress?.summary) setLastSummary({ summary: state.progress.summary, runId: state.runId });
    onChanged();
  });

  useEffect(() => {
    getFamilySettings()
      .then((data) => {
        setSettings(data);
        setModel(data.model);
      })
      .catch(() => setSettings(null));
  }, []);

  // Once the pass has written families, the normal run keeps them.
  const passWrote = useMemo(
    () => Boolean(overview?.families.some((f) => f.members.some((m) => m.from_pass))),
    [overview],
  );
  useEffect(() => {
    setMode(passWrote ? "incremental" : "rebuild");
  }, [passWrote, target]);

  // A different run is a different price; a different bank also drops the last result.
  useEffect(() => {
    setEstimate(null);
  }, [target, mode, model, dryRun]);
  useEffect(() => {
    setLastSummary(null);
  }, [target]);

  const options = useMemo(() => modelOptions(settings?.models || [], model), [settings, model]);
  const handOff = Boolean(settings?.flags?.ORION1_PROJECT_FAMILIES);
  const writeBlocked = mode === "incremental" && !dryRun && !handOff;

  const changeModel = async (next: string) => {
    setModel(next);
    try {
      await saveFamilyModel(next);
    } catch (e) {
      toast(formatApiError(e), true);
    }
  };

  const loadEstimate = useCallback(async () => {
    setBusy(true);
    try {
      setEstimate(await estimateFamilies({ target, mode, model }));
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  }, [mode, model, target, toast]);

  const start = async () => {
    setBusy(true);
    setLastSummary(null);
    try {
      const accepted = await startFamilyClassification({ target, mode, model, dry_run: dryRun });
      setEstimate(null);
      job.start(accepted.run_id);
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  const savePrefix = async () => {
    try {
      const saved = await setFamilyPrefix(target, prefixDraft.trim());
      toast(saved.prefix ? `Family ID prefix set to ${saved.prefix}.` : "Prefix cleared: the next run chooses one.");
      setEditingPrefix(false);
      onChanged();
    } catch (e) {
      toast(formatApiError(e), true);
    }
  };

  const progress = job.state?.progress;
  const prefix = overview?.prefix || "";
  const lastRun = overview?.last_run;
  const blockedReason = estimate
    ? estimate.over_input_cap
      ? estimate.too_large_reason ||
        `${estimate.projects} projects are more than one call takes (at most ${estimate.max_projects}); splitting a bank by theme is not built yet.`
      : estimate.projects === 0
        ? "This bank has no active projects."
        : mode === "incremental" && estimate.incremental_blocked
          ? `The stored family IDs are inconsistent (${estimate.inconsistencies.slice(0, 2).join("; ")}): run a rebuild first.`
          : ""
    : "";

  return (
    <div className="px-6 pb-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-data-label">
          Project families
        </span>
        <Select
          value={mode}
          disabled={job.running}
          onChange={(e) => setMode(e.target.value as Mode)}
          aria-label="Family run mode"
          className="py-1 w-auto"
        >
          <option value="incremental">Incremental (keep families and IDs)</option>
          <option value="rebuild">Rebuild (renumber from 01)</option>
        </Select>
        <label className="flex items-center gap-2 text-body-sm text-on-surface-variant" title="Compute and log everything, write nothing">
          <input type="checkbox" checked={dryRun} disabled={job.running} onChange={(e) => setDryRun(e.target.checked)} />
          Dry run
        </label>
        <div className="flex items-center gap-2 min-w-[14rem]">
          <Select
            value={model}
            disabled={!settings || job.running}
            onChange={(e) => void changeModel(e.target.value)}
            aria-label="Model used to classify families"
            className="py-1"
          >
            {options.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id === settings?.default_model ? `${m.name || m.id} (default)` : m.name || m.id}
              </option>
            ))}
          </Select>
        </div>
        <Button
          variant="primary"
          size="sm"
          disabled={busy || job.running || !target || writeBlocked}
          onClick={() => void loadEstimate()}
          title={
            writeBlocked
              ? "Incremental runs that write need ORION1_PROJECT_FAMILIES on: synthesis still writes the family fields"
              : "One model call groups every active project of this bank into families and marks duplicates"
          }
        >
          <Icon name="account_tree" className="text-base" />
          Classify families
        </Button>
        <span className="text-body-sm font-data-mono text-on-surface-variant">
          prefix{" "}
          {editingPrefix ? (
            <>
              <Input
                value={prefixDraft}
                onChange={(e) => setPrefixDraft(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && void savePrefix()}
                className="inline-block w-20 py-0.5"
                maxLength={4}
                aria-label="Family ID prefix"
              />{" "}
              <Button variant="link-muted" onClick={() => void savePrefix()}>
                Save
              </Button>{" "}
              <Button variant="link-muted" onClick={() => setEditingPrefix(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              {prefix || "not set"}{" "}
              <Button
                variant="link-muted"
                disabled={job.running}
                onClick={() => {
                  setPrefixDraft(prefix);
                  setEditingPrefix(true);
                }}
              >
                Change
              </Button>
            </>
          )}
        </span>
        {lastRun ? (
          <span className="text-[11px] font-data-mono text-on-surface-variant">
            last run: {lastRun.dry_run ? "dry run" : lastRun.mode} · {lastRun.status}
            {lastRun.trigger === "after_synthesis" ? " · after synthesis" : ""}
            {lastRun.finished_at ? ` · ${new Date(lastRun.finished_at).toLocaleString()}` : ""}
          </span>
        ) : null}
      </div>

      {estimate ? (
        <div className="border border-primary/30 bg-primary/5 px-3 py-2 text-body-sm space-y-1">
          <p className="text-on-surface">
            {estimate.projects} projects of {estimate.target_company} · one call with {shortModel(estimate.model)} ·
            estimated {usd(estimate.estimate_usd, 3)} (at most {usd(estimate.max_usd, 3)} with a retry)
            {estimate.remaining_usd != null ? ` · ${usd(estimate.remaining_usd)} budget left` : ""}
            {estimate.existing_families ? ` · ${estimate.existing_families} families stored` : ""}
          </p>
          <p className="text-[11px] text-on-surface-variant">
            {mode === "rebuild"
              ? "Rebuild: families are grouped from scratch and numbered from 01; today's values are hints only."
              : "Incremental: stored families and IDs are kept; new families get the next free number."}{" "}
            {dryRun ? "Dry run: nothing is written; review the result below." : "Every change is written as a project revision and can be reverted."}
          </p>
          {blockedReason ? <p className="text-[11px] text-error">{blockedReason}</p> : null}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button variant="primary" size="sm" disabled={busy || Boolean(blockedReason)} onClick={() => void start()}>
              {dryRun ? "Start dry run" : mode === "rebuild" ? "Rebuild and write" : "Classify and write"}
            </Button>
            <Button variant="link-muted" onClick={() => setEstimate(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {job.running ? (
        <div className="flex flex-wrap items-center gap-3 border border-outline-variant bg-surface-container px-3 py-2 text-body-sm">
          <span className="orion-spinner" aria-hidden />
          <span className="font-data-mono text-on-surface">
            {STAGE_LABELS[progress?.stage || ""] || "Starting"}
            {progress?.projects ? ` · ${progress.projects} projects` : ""}
            {progress?.stage === "writing" ? ` · ${progress.written}/${progress.to_write} written` : ""}
            {progress ? ` · ${usd(progress.cost_usd, 3)}` : ""}
          </span>
          <Button
            variant="link-muted"
            onClick={() => {
              if (job.state?.runId) cancelJob(job.state.runId).catch((e) => toast(formatApiError(e), true));
            }}
            title="Stops before anything is written"
          >
            Stop
          </Button>
        </div>
      ) : null}

      {lastSummary ? (
        <div className="border border-outline-variant bg-surface-container px-3 py-2 space-y-1">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-data-label">
              Family run result
            </span>
            <Button variant="link-muted" onClick={() => setLastSummary(null)}>
              Hide
            </Button>
          </div>
          <RunSummary summary={lastSummary.summary} runId={lastSummary.runId} />
        </div>
      ) : null}
    </div>
  );
}
